import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getRemoteVenueSummary,
  requestRemoteState,
  retryRemoteVenueGigAction,
  runRemoteVenueGigAction,
} from '../../components/Remote/deviceRemoteApi';
import { getVenueNowPlaying } from './venueNowPlaying';
import { refreshVenueInputReports, retainVenueScreenReports } from './venueInputReports';
import { resolveScreenContext } from './launcherTopology';
import VenuePlaybackControls from './VenuePlaybackControls';
import ScreenPreview from '../../components/AreaOverview/ScreenPreview';
import useScreenPreviews from '../../components/AreaOverview/useScreenPreviews';

const REFRESH_INTERVAL_MS = 15000;

async function requestVenuePreview({ deviceId, timeout }) {
  try {
    const response = await requestRemoteState({ targetDeviceId: deviceId, includeScreenshot: true }, { timeout });
    return response?.state || {};
  } catch (error) {
    // Preserve native capture error codes through the device-JWT API envelope.
    const detail = error.payload?.error;
    throw detail ? { ...error, screenshotError: detail.screenshotError || detail.error || detail.message || error.message } : error;
  }
}
const GIG_ACTION_LABELS = {
  ready: 'Ready',
  band_started: 'Band started',
  break: 'Break',
  resume: 'Resume',
  finish: 'Finish',
  cancelled: 'Cancelled',
};

export function getVisibleGigActions(liveGig) {
  return (Array.isArray(liveGig?.validActions) ? liveGig.validActions : [])
    .filter((action) => GIG_ACTION_LABELS[action])
    .map((action) => ({ key: action, label: GIG_ACTION_LABELS[action] }));
}

function createRuntimeRequestKey(occurrenceId, action) {
  const unique = globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `phone:${occurrenceId}:${action}:${unique}`;
}

export function formatVenueTime(value, timeZone) {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat([], {
      timeZone: timeZone || 'UTC',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}

export function getVenueSourceHealth(sources = {}) {
  const entries = Object.entries(sources || {}).filter(([name, source]) =>
    !(name === 'observations' && source?.totalTargets === 0)
  );
  const unavailable = entries.filter(([, source]) => source?.status === 'unavailable').map(([name]) => name);
  const partial = entries.filter(([, source]) => source?.status === 'partial').map(([name]) => name);
  return {
    degraded: unavailable.length > 0 || partial.length > 0,
    unavailable,
    partial,
  };
}

export function describeObservation(observation) {
  if (!observation || observation.status === 'unavailable') return 'No recent report';
  if (observation.status === 'stale') return 'Last report is out of date';
  if (observation.foreground?.isSuitestream === false && observation.foreground?.appLabel) {
    return observation.foreground.appLabel;
  }
  const title = observation.playback?.deckA?.title;
  if (title) return title;
  if (observation.playback?.isPlaying === true) return 'Playback reported';
  if (observation.foreground?.appLabel) return observation.foreground.appLabel;
  if (observation.foreground?.isSuitestream) return 'Suitestream is ready';
  return 'Online';
}

function EvidenceBadge({ evidence }) {
  const label = {
    observed: 'Observed',
    recorded: 'Live session',
    scheduled: 'Scheduled',
    desired: 'Expected',
    booking: 'Booking',
  }[evidence] || 'Status';
  return <span className={`venue-dashboard__evidence venue-dashboard__evidence--${evidence || 'status'}`}>{label}</span>;
}

function EndpointStatus({ label, intended, observation, onOpenRemote, targetDeviceId, screenId }) {
  const observationStatus = observation?.status || 'unavailable';
  return (
    <div className="venue-dashboard__endpoint">
      <div className="venue-dashboard__endpoint-heading">
        <strong>{label}</strong>
        <span className={`venue-dashboard__status-dot venue-dashboard__status-dot--${observationStatus}`}>
          {observationStatus === 'live' ? 'Reporting' : observationStatus === 'stale' ? 'Stale' : 'Unknown'}
        </span>
      </div>
      <dl>
        <div><dt>Intended</dt><dd>{intended}</dd></div>
        <div><dt>Observed</dt><dd>{describeObservation(observation)}</dd></div>
      </dl>
      {observationStatus !== 'live' && targetDeviceId ? (
        <button type="button" onClick={() => onOpenRemote(targetDeviceId, screenId)}>Open controls</button>
      ) : null}
    </div>
  );
}

export default function VenueDashboard({ areaId, selectedTargetId, selectedScreenId, controlsBlocked, enabled, previewsPaused = false, onAction, onOpenRemote, onOpenSchedule, addSomething }) {
  const [storedSummary, setSummary] = useState(null);
  const [summaryArea, setSummaryArea] = useState(null);
  const summary = summaryArea === String(areaId) ? storedSummary : null;
  const scope = useMemo(() => ({ areaId, enabled }), [areaId, enabled]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const loadingScope = useRef(null);
  const summaryRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [inputsReady, setInputsReady] = useState(false);
  const [message, setMessage] = useState('');
  const [runtimeBusy, setRuntimeBusy] = useState('');
  const [runtimeMessage, setRuntimeMessage] = useState('');
  const [includeWelcome, setIncludeWelcome] = useState(true);
  const [showAllScreens, setShowAllScreens] = useState(false);
  // Venue presents the latest completed observation batch. Do not age its artwork
  // into an unknown state on a one-second timer while the next batch is loading.
  const nowPlaying = useMemo(() => getVenueNowPlaying(summary), [summary]);
  const selectedScreen = resolveScreenContext(summary?.screens || [], selectedTargetId, selectedScreenId);
  const selectedScreenIdForPlayback = selectedScreen?.id;
  const visibleNowPlaying = useMemo(() => showAllScreens
    ? nowPlaying
    : nowPlaying.filter((entry) => Number(entry.screenId) === Number(selectedScreenIdForPlayback)),
  [nowPlaying, showAllScreens, selectedScreenIdForPlayback]);

  const previews = useScreenPreviews({
    entries: visibleNowPlaying, scopeKey: String(areaId), enabled: enabled && !controlsBlocked && !previewsPaused,
    ready: Boolean(summary) && status === 'ready' && inputsReady, requestState: requestVenuePreview,
  });

  useEffect(() => {
    setShowAllScreens(false);
  }, [areaId, selectedTargetId, selectedScreenId]);

  const loadSummary = useCallback(async ({ background = false } = {}) => {
    if (!enabled || loadingScope.current === scope || currentScope.current !== scope) return;
    loadingScope.current = scope;
    if (!background) setStatus((current) => current === 'ready' ? 'refreshing' : 'loading');
    try {
      const response = await getRemoteVenueSummary(areaId);
      if (currentScope.current !== scope) return;
      const previous = summaryRef.current?.areaId === String(areaId) ? summaryRef.current.data : null;
      const result = retainVenueScreenReports(response, previous);
      setInputsReady(false);
      // Bootstrap information immediately; background updates publish once native
      // reads finish so an older raw summary cannot briefly erase the artwork.
      if (!previous) {
        summaryRef.current = { areaId: String(areaId), data: result };
        setSummary(result);
        setSummaryArea(String(areaId));
        setStatus('ready');
      }
      const refreshed = await refreshVenueInputReports(result);
      if (currentScope.current !== scope) return;
      summaryRef.current = { areaId: String(areaId), data: refreshed };
      setSummary(refreshed);
      setSummaryArea(String(areaId));
      setMessage('');
      setStatus('ready');
      setInputsReady(true);
    } catch (error) {
      if (currentScope.current !== scope) return;
      setMessage(error.message || 'Venue status is unavailable.');
      setStatus(summaryRef.current?.areaId === String(areaId) ? 'stale' : 'error');
    } finally { if (loadingScope.current === scope) loadingScope.current = null; }
  }, [areaId, enabled, scope]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const refresh = async (options) => {
      if (cancelled || (options?.background && document.visibilityState === 'hidden')) return;
      await loadSummary(options);
    };
    refresh();
    const timer = window.setInterval(() => refresh({ background: true }), REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (currentScope.current === scope) currentScope.current = null;
      window.clearInterval(timer);
    };
  }, [enabled, loadSummary, scope]);

  const sourceHealth = useMemo(() => getVenueSourceHealth(summary?.sources), [summary?.sources]);
  const timeZone = summary?.venue?.timeZone || 'UTC';
  const liveGig = summary?.liveGig || null;
  const gigActions = getVisibleGigActions(liveGig);

  const applyGigAction = async (actionKey) => {
    if (!liveGig?.occurrenceId || runtimeBusy) return;
    if (['finish', 'cancelled'].includes(actionKey)) {
      const prompt = actionKey === 'cancelled'
        ? 'Cancel this gig? Future generated promotions will be stopped.'
        : 'Finish this live gig and restore the current venue music?';
      if (!window.confirm(prompt)) return;
    }
    setRuntimeBusy(actionKey);
    setRuntimeMessage('');
    try {
      const response = await runRemoteVenueGigAction({
        occurrenceId: liveGig.occurrenceId,
        action: actionKey,
        requestKey: createRuntimeRequestKey(liveGig.occurrenceId, actionKey),
        includeWelcome,
      });
      setSummary((current) => ({ ...current, liveGig: response.liveGig }));
      const outcome = response.action?.status;
      setRuntimeMessage(outcome === 'complete'
        ? `${GIG_ACTION_LABELS[actionKey]} completed.`
        : `${GIG_ACTION_LABELS[actionKey]} was recorded, but some targets need attention.`);
    } catch (error) {
      setRuntimeMessage(error.message || 'The live gig action failed.');
    } finally {
      setRuntimeBusy('');
    }
  };

  const retryGigAction = async () => {
    const action = liveGig?.lastAction;
    if (!liveGig?.occurrenceId || !action?.id || runtimeBusy) return;
    setRuntimeBusy('retry');
    setRuntimeMessage('');
    try {
      const response = await retryRemoteVenueGigAction({
        occurrenceId: liveGig.occurrenceId,
        actionId: action.id,
      });
      setSummary((current) => ({ ...current, liveGig: response.liveGig }));
      setRuntimeMessage(response.action?.status === 'complete'
        ? 'The transition retry completed.'
        : 'The retry was recorded, but some targets still need attention.');
    } catch (error) {
      setRuntimeMessage(error.message || 'The transition retry failed.');
    } finally {
      setRuntimeBusy('');
    }
  };

  if (!summary && status === 'loading') {
    return <div className="venue-dashboard__state">Loading Venue status…</div>;
  }
  if (!summary && status === 'error') {
    return (
      <div className="venue-dashboard__state venue-dashboard__state--error">
        <strong>Venue status could not be loaded</strong>
        <span>{message}</span>
        <button type="button" onClick={() => loadSummary()}>Try again</button>
      </div>
    );
  }
  if (!summary) return null;

  const next = summary.next || {};
  const nextDetail = next.kind === 'gig' ? next.detail : null;
  const attention = Array.isArray(summary.attention) ? summary.attention : [];
  const screens = Array.isArray(summary.screens) ? summary.screens : [];
  const audioEndpoints = Array.isArray(summary.audioEndpoints) ? summary.audioEndpoints : [];
  const today = Array.isArray(summary.today) ? summary.today : [];

  return (
    <div className="venue-dashboard">
      <header className="venue-dashboard__heading">
        <div>
          <span>{summary.venue?.areaName || 'Venue'}</span>
          <h1>{summary.venue?.name || 'Venue'}</h1>
        </div>
        <button type="button" onClick={() => loadSummary()} disabled={status === 'refreshing'}>
          {status === 'refreshing' ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {status === 'stale' ? (
        <div className="venue-dashboard__banner venue-dashboard__banner--warning">
          Showing the last Venue status. {message}
        </div>
      ) : null}
      {sourceHealth.degraded ? (
        <div className="venue-dashboard__banner">
          Some live details are unavailable. Known information is still shown below.
        </div>
      ) : null}

      <section className="venue-dashboard__now">
        <div className="venue-dashboard__section-label">
          <span>Now Playing</span>
          <button type="button" aria-pressed={showAllScreens} onClick={() => setShowAllScreens((current) => !current)}>
            {showAllScreens ? 'Selected Screen' : 'All Screens'}
          </button>
        </div>
        {visibleNowPlaying.length ? visibleNowPlaying.map((entry) => (
          <div key={`${entry.screenId}:${entry.controlDeviceId}`}>
            <div className="venue-dashboard__playing">
              <div className="venue-dashboard__playing-copy">
                {showAllScreens ? <span>{entry.screenName}</span> : null}
                <h2>{entry.title}</h2>
                {entry.nowPlaying.activePort ? <p>HDMI {entry.nowPlaying.activePort}{entry.nowPlaying.confirmed ? '' : ' · unverified'}</p>
                  : entry.nowPlaying.reportedDeviceId ? <p>Input unverified</p> : null}
              </div>
              <ScreenPreview nowPlaying={entry.nowPlaying} target={entry.target} preview={previews[entry.screenId]} />
            </div>
            <VenuePlaybackControls
              entry={entry}
              target={summary.playbackDevices?.find((target) => Number(target.id) === entry.controlDeviceId)}
              blocked={controlsBlocked || summary.activeExperience?.participatingDeviceIds?.includes(entry.controlDeviceId)}
              onRefresh={() => loadSummary({ background: true })}
            />
          </div>
        )) : <p>{!showAllScreens && !selectedScreen ? 'Choose a screen to see what is playing.' : 'No screen playback reports available.'}</p>}
        {summary.activeExperience ? (
          <button type="button" onClick={() => onOpenRemote(summary.activeExperience.primaryDeviceId)}>
            Open {summary.activeExperience.pluginKey === 'jukebox' ? 'Jukebox' : 'Karaoke'} controls
          </button>
        ) : null}
      </section>

      {liveGig ? (
        <section className="venue-dashboard__card venue-dashboard__gig-runtime">
          <div className="venue-dashboard__section-label">
            <span>Live gig controls</span>
            <strong>{String(liveGig.runtimeStatus || 'pending').replace('_', ' ')}</strong>
          </div>
          <h2>{liveGig.title}</h2>
          <p>
            {formatVenueTime(liveGig.startsAtUtc, timeZone)}–{formatVenueTime(liveGig.endsAtUtc, timeZone)}
          </p>
          {gigActions.some((action) => action.key === 'band_started') ? (
            <label className="venue-dashboard__welcome-choice">
              <input
                type="checkbox"
                checked={includeWelcome}
                onChange={(event) => setIncludeWelcome(event.target.checked)}
              />
              Play the generated welcome announcement
            </label>
          ) : null}
          {gigActions.length ? (
            <div className="venue-dashboard__gig-actions">
              {gigActions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className={action.key === 'cancelled' ? 'venue-dashboard__danger-action' : ''}
                  disabled={Boolean(runtimeBusy)}
                  onClick={() => applyGigAction(action.key)}
                >
                  {runtimeBusy === action.key ? 'Working…' : action.label}
                </button>
              ))}
            </div>
          ) : <p className="venue-dashboard__quiet">No further stage action is available.</p>}
          {['partial', 'failed'].includes(liveGig.lastAction?.status) ? (
            <button
              type="button"
              className="venue-dashboard__retry-action"
              disabled={Boolean(runtimeBusy)}
              onClick={retryGigAction}
            >
              {runtimeBusy === 'retry' ? 'Retrying…' : `Retry ${GIG_ACTION_LABELS[liveGig.lastAction.actionKey] || 'transition'}`}
            </button>
          ) : null}
          {runtimeMessage ? <div className="venue-dashboard__runtime-message" role="status">{runtimeMessage}</div> : null}
          {liveGig.lastAction ? (
            <div className={`venue-dashboard__runtime-outcome venue-dashboard__runtime-outcome--${liveGig.lastAction.status}`}>
              Last action: {GIG_ACTION_LABELS[liveGig.lastAction.actionKey] || liveGig.lastAction.actionKey}
              {' · '}{liveGig.lastAction.status}
            </div>
          ) : null}
        </section>
      ) : null}

      {addSomething ? (
        <section className="venue-dashboard__add-something">
          {typeof addSomething === 'function'
            ? addSomething({ areaId: summary.venue?.areaId, screens })
            : addSomething}
        </section>
      ) : null}

      <section className="venue-dashboard__card venue-dashboard__next">
        <div className="venue-dashboard__section-label"><span>Next</span><EvidenceBadge evidence={next.evidence} /></div>
        <div className="venue-dashboard__next-row">
          <div>
            <h2>{next.label || 'Nothing else planned'}</h2>
            {nextDetail ? <p>{nextDetail}</p> : null}
          </div>
          {next.startTime ? <time dateTime={next.startTime}>{formatVenueTime(next.startTime, timeZone)}</time> : null}
        </div>
        <button type="button" className="venue-dashboard__text-action" onClick={onOpenSchedule}>View schedule</button>
      </section>

      <section className="venue-dashboard__card">
        <div className="venue-dashboard__section-label">
          <span>Attention</span>
          <strong>{attention.length ? attention.length : 'All clear'}</strong>
        </div>
        {attention.length ? (
          <div className="venue-dashboard__attention-list">
            {attention.map((item) => (
              <div key={item.id} className={`venue-dashboard__attention venue-dashboard__attention--${item.severity || 'warning'}`}>
                <div><strong>{item.title}</strong>{item.detail ? <span>{item.detail}</span> : null}</div>
                {item.action ? <button type="button" onClick={() => onAction(item.action)}>{item.action.label}</button> : null}
              </div>
            ))}
          </div>
        ) : <p className="venue-dashboard__quiet">Nothing currently needs your attention.</p>}
      </section>

      <section className="venue-dashboard__card">
        <div className="venue-dashboard__section-label"><span>Screens &amp; Audio</span><strong>{screens.length + audioEndpoints.length}</strong></div>
        {screens.length || audioEndpoints.length ? (
          <div className="venue-dashboard__endpoint-list">
            {screens.map((screen) => (
              <EndpointStatus
                key={`screen-${screen.id}`}
                label={screen.name || 'Screen'}
                intended={screen.playbackTargetId ? 'Suitestream playback configured' : 'Playback not configured'}
                observation={screen.observation}
                targetDeviceId={screen.playbackTargetId}
                screenId={screen.id}
                onOpenRemote={onOpenRemote}
              />
            ))}
            {audioEndpoints.map((endpoint) => (
              <EndpointStatus
                key={`audio-${endpoint.id}`}
                label={endpoint.name || 'Audio'}
                intended={endpoint.sourceDeviceId ? 'Audio source configured' : 'Source not configured'}
                observation={endpoint.observation}
                targetDeviceId={endpoint.sourceDeviceId}
                onOpenRemote={onOpenRemote}
              />
            ))}
          </div>
        ) : <p className="venue-dashboard__quiet">No logical Screens or Audio Endpoints are configured yet.</p>}
      </section>

      <section className="venue-dashboard__card venue-dashboard__today">
        <div className="venue-dashboard__section-label"><span>Today</span><strong>{today.length}</strong></div>
        {today.length ? today.map((item) => (
          <div className="venue-dashboard__today-row" key={item.occurrenceKey || `${item.type}-${item.id}-${item.startTime}`}>
            <time dateTime={item.startTime}>{formatVenueTime(item.startTime, timeZone)}</time>
            <div><strong>{item.label}</strong>{item.status === 'cancelled' ? <span>Cancelled</span> : null}</div>
            <EvidenceBadge evidence={item.evidence} />
          </div>
        )) : <p className="venue-dashboard__quiet">No events or scheduled changes today. Default rotation continues.</p>}
      </section>

      <footer className="venue-dashboard__updated">
        Updated {formatVenueTime(summary.generatedAt, timeZone)}
      </footer>
    </div>
  );
}
